import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Link as LinkIcon, Code } from 'lucide-react';
import { toast } from 'sonner';
import { createPaymentButton } from '../../lib/api';

const MerchantButtonsTab = ({ buttons, setButtons }) => {
  const [createButtonOpen, setCreateButtonOpen] = useState(false);
  const [buttonForm, setButtonForm] = useState({
    name: 'Pay Now',
    amount: '',
    currency: 'USD',
    description: '',
    success_url: '',
    cancel_url: ''
  });

  const handleCreateButton = async (e) => {
    e.preventDefault();
    if (!buttonForm.name) {
      toast.error('Button name is required');
      return;
    }
    
    try {
      const data = {
        ...buttonForm,
        amount: buttonForm.amount ? parseFloat(buttonForm.amount) : null
      };
      const res = await createPaymentButton(data);
      setButtons([...buttons, res.data]);
      setCreateButtonOpen(false);
      setButtonForm({
        name: 'Pay Now',
        amount: '',
        currency: 'USD',
        description: '',
        success_url: '',
        cancel_url: ''
      });
      toast.success('Payment button created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create button');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="space-y-6 mt-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-heading font-semibold">Payment Buttons</h2>
        <Dialog open={createButtonOpen} onOpenChange={setCreateButtonOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="create-button-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Button
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Payment Button</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateButton} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="btn_name">Button Name *</Label>
                <Input
                  id="btn_name"
                  value={buttonForm.name}
                  onChange={(e) => setButtonForm({ ...buttonForm, name: e.target.value })}
                  placeholder="Pay Now"
                  data-testid="button-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="btn_amount">Amount (optional)</Label>
                  <Input
                    id="btn_amount"
                    type="number"
                    step="0.01"
                    value={buttonForm.amount}
                    onChange={(e) => setButtonForm({ ...buttonForm, amount: e.target.value })}
                    placeholder="Leave empty for custom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="btn_currency">Currency</Label>
                  <select
                    id="btn_currency"
                    value={buttonForm.currency}
                    onChange={(e) => setButtonForm({ ...buttonForm, currency: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="btn_desc">Description</Label>
                <Input
                  id="btn_desc"
                  value={buttonForm.description}
                  onChange={(e) => setButtonForm({ ...buttonForm, description: e.target.value })}
                  placeholder="Payment for..."
                />
              </div>
              <Button type="submit" className="w-full rounded-xl" data-testid="submit-button-btn">
                Create Button
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {buttons.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <LinkIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No payment buttons yet</p>
            <p className="text-sm text-muted-foreground">Create your first button to start accepting payments</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {buttons.map((button) => (
            <Card key={button.id} data-testid={`button-card-${button.id}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{button.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {button.amount ? `${button.currency} ${button.amount}` : 'Custom amount'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {button.total_payments} payments • ${button.total_amount?.toFixed(2) || '0.00'} total
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(button.payment_link, 'Payment link')}
                      className="rounded-lg"
                    >
                      <LinkIcon className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(button.button_code, 'Embed code')}
                      className="rounded-lg"
                    >
                      <Code className="w-4 h-4 mr-1" />
                      Copy Code
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MerchantButtonsTab;
